import { type ReactNode, useState } from 'react';
import { ApiError } from '@/api/client';
import { useExternalAuthProviders, useStartExternalLogin } from '@/api/queries/auth';
import { FormAlert } from '@/features/auth/AuthLayout';
import { providerLabel, rememberPendingExternalAuth } from '@/features/auth/external-provider';

/**
 * Connexion par fournisseur externe (LinkedIn, Google).
 *
 * Les boutons sont construits à partir de la liste renvoyée par l'API, jamais écrits en
 * dur : seul le serveur sait quels fournisseurs sont activés ET complètement configurés sur
 * cet environnement. Un bouton actif qui ne mène nulle part est pire qu'un bouton absent —
 * il fait perdre une tentative de connexion et laisse croire à une panne.
 *
 * Le clic ne construit AUCUNE URL : il en demande une au serveur, qui y place les secrets
 * anti-rejeu de la tentative dans un cookie inaccessible au JavaScript.
 */
export function SocialAuthButtons() {
  const providers = useExternalAuthProviders();
  const start = useStartExternalLogin();
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);

  async function connect(provider: string) {
    setPendingProvider(provider);

    try {
      const { authorizationUrl } = await start.mutateAsync({ provider });

      // Mémorisé AVANT la redirection : au retour, l'URI d'arrivée étant fixe, c'est la
      // seule chose qui dise à l'écran de retour ce qu'il était en train de faire.
      rememberPendingExternalAuth({ intent: 'login', provider });

      // `assign` et non `replace` : le bouton « précédent » du navigateur doit ramener à
      // l'écran de connexion si la personne change d'avis chez le fournisseur.
      window.location.assign(authorizationUrl);
    } catch {
      // L'erreur est affichée par `alertMessage` ; l'état de chargement, lui, doit être
      // relâché, sinon le bouton reste bloqué alors que rien ne se passe.
      setPendingProvider(null);
    }
  }

  // Rien à afficher tant que la liste n'est pas connue, et rien du tout si aucun
  // fournisseur n'est configuré : ni bouton grisé, ni promesse de disponibilité future.
  if (providers.data === undefined || providers.data.length === 0) {
    return null;
  }

  const alertMessage =
    start.error instanceof ApiError
      ? (start.error.code === 'auth.external_provider_unavailable'
        ? "Ce mode de connexion n'est pas disponible pour le moment."
        : start.error.message)
      : null;

  return (
    <div className="flex flex-col gap-2.5">
      {alertMessage === null ? null : <FormAlert message={alertMessage} />}

      {providers.data.map((provider) => (
        <ProviderButton
          key={provider}
          /* « Continuer avec » plutôt que « Se connecter avec » : le même bouton sert à
             rejoindre le compte et à s'y reconnecter, sans que l'utilisateur ait à choisir. */
          label={`Continuer avec ${providerLabel(provider)}`}
          icon={PROVIDER_ICONS[provider] ?? null}
          loading={pendingProvider === provider}
          disabled={pendingProvider !== null}
          onClick={() => void connect(provider)}
        />
      ))}
    </div>
  );
}

export function ProviderButton({
  label,
  icon,
  loading,
  disabled,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-11 w-full items-center justify-center gap-2.5 rounded-xl bg-white text-[13px] font-medium text-ink-900 ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:text-ink-300"
    >
      {icon}
      {loading ? 'Redirection…' : label}
    </button>
  );
}

/* Les logos sont recopiés en SVG plutôt que chargés depuis un CDN : les conditions
   d'utilisation des deux marques imposent un tracé exact, et un logo servi par un tiers
   disparaît le jour où le tiers change d'URL — sur l'écran de connexion, précisément. */

function GoogleIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3.01h3.89c2.27-2.09 3.57-5.17 3.57-8.82Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.89-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.11A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.27a7.2 7.2 0 0 1 0-4.54V6.62H1.26a12 12 0 0 0 0 10.76l4.01-3.11Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.77c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.26 6.62l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77Z"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0">
      <path
        fill="#0A66C2"
        d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13Zm1.78 13.02H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z"
      />
    </svg>
  );
}

/** Un fournisseur sans logo connu reste utilisable : le libellé suffit. */
const PROVIDER_ICONS: Record<string, ReactNode> = {
  linkedin: <LinkedInIcon />,
  google: <GoogleIcon />,
};

/** Logo d'un fournisseur, réutilisé par Paramètres › Préférences. */
export function ProviderIcon({ provider }: { provider: string }) {
  return PROVIDER_ICONS[provider] ?? null;
}
